import { memo, Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from "react-i18next"
import { PageHeader, PenModal, NewSectionModal, ToggleBox, NewModal, MainContentPaddingTypes, Overlay, SectionModal, NewButtonList, NewSelect, NewInput, AreaLayout } from "../../../../Components"
import { colors, fonts, frames, glyphs, useMediaQuery } from '../../../../Styles';
import { ButtonTypes, NewFieldStyles, NewFieldTypes, SystemTypes } from '../../../../Definitions';
import { useAppSelector } from '../../../../Store';
import { Graph_QL, useSection, useSystem, Location, SectionsInfo } from '../../../../Services';
import { Pages } from '../../../../Navigation/root';
import { BluePrint } from '../../../../Assets/Glyphs/bluePrint';
import { selectByLocations } from '../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/AnimalDataSlices/animalsSlice';
import { currentLfViewTypes, ILfContext, LfContext, LfDispatcherT, LfReducer } from '../../../../Components/LocationFeeder/Modals/_Modals/lfPenContext';
import LF_PENLIST from '../../../../Components/LocationFeeder/SubComponents/lfPenList';
import EditSectionModal from '../../../../Components/LocationFeeder/Modals/editSectionModal';
import { LoadingSpinner } from '../../../../Services/wrapper';
import BluePrintModal from '../../../../Components/bluePrintModal';
import { selectAllLocation } from '../../../../Services/Agrisys_GqlDataCaller/gqlData_Reducer';
import { isEqual } from 'lodash';

// #region | ButtonList Contents and Styles
export const getButtonStyles = (index: number, editTable: boolean) => {
  if (editTable && index === 2) {
    return { backgroundColor: colors.greyPrimary, opacity: 1 };
  }
  return { backgroundColor: colors.greySecondary, opacity: 0.5 };
};

export const contents = (
  func_add: () => void,
  func_edit: () => void,
  func_cancel: () => void,
  label_add: string,
  label_edit: string,
  label_cancel: string,
  editTable: boolean
): [string[], JSX.Element[], (() => void)[]] => {
  const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];
  
  if (editTable) {
    buttonEntries.push(
      { label: label_cancel, glyphs: glyphs.Close({}), onClick: func_cancel }
    );
  }
    
  buttonEntries.push(
    { label: label_add, glyphs: glyphs.Add({}), onClick: func_add },
    { label: label_edit, glyphs: glyphs.Edit({}), onClick: func_edit }
  );

  const labels = buttonEntries.map((b) => b.label);
  const icons = buttonEntries.map((b) => b.glyphs)
  const onclicks = buttonEntries.map((b) => b.onClick);
  return [labels, icons, onclicks];
};
// #endregion

const penContentOptions = [
  { id: 0, label: "animal number" },
  { id: 1, label: "pen name" }
]

export default function FreedaLocationFeeder() {
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 450px)`);
  const [currentViewType, setCurrentViewType] = useState<"TODAY" | "YESTERDAY" | "ATTENTIONS" | "BASIC">("TODAY");
  const [activeSection, setActiveSection] = useState<SectionsInfo | undefined>();
  const [activePen, setActivePen] = useState<Location | undefined>();
  const [shouldDispatch, setShouldDispatch] = useState(false);
  const [loading, setLoading] = useState(true);
  // const [animalsInSection, setAnimalsInSection] = useState<AnimalInfo[]>([]);

  const [editSections, setEditSections] = useState(false)
  const [newSectionModal, setNewSectionModal] = useState(false)
  const [bluePrintModal, setBluePrintModal] = useState<boolean>(false)
  
  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm });
    let connection = Graph_QL.create({ domain: farm });
    return { domain, connection };
  }, [farm]);
  
  const { call: getLfSectionsInfo, filteredBySystemId, filteredBySystemIdSectionIndex, getPensFromSectionIdPenIndex } = useSection(connection);
  const { call: getSystemCall, data: systemsState } = useSystem(connection); 
  const { systemId: urlSystemId } = useParams();
  const systemId = Number(urlSystemId)

  // Data gets
  useEffect(() => {
    async function fetchSections() {
      setShouldDispatch(false);
      setLoading(true);
      await getLfSectionsInfo({ ids: [systemId] }, connection);
      setShouldDispatch(true);
      setLoading(false);
    }
    fetchSections()
    getSystemCall({ ids: [systemId] });
  }, [connection, domain, systemId])

  // System
  const system = useMemo(() => systemsState[domain]?.data.find((system) => system.system_id === systemId), [domain, systemId, systemsState])

  // Sections
  const sectionLocations = useAppSelector(selectAllLocation);
  const sections = useMemo(() => filteredBySystemId(domain, systemId).map((section) => ({...section, section_locations: (sectionLocations?.filter((s_l) => s_l.sectionId === section.section_id)??[]) as Location[]})), [domain, filteredBySystemId, sectionLocations, systemId])
  
    
  // Current Section
  const { index: currentSectionIndex, name: currentSectionName, pens } = filteredBySystemIdSectionIndex(domain, systemId, activeSection?.section_id ?? 0)

  const [currentSectionPens, setCurrentSectionPens] = useState(pens);

  useEffect(() => {
    setCurrentSectionPens((old) => {if (isEqual(pens, old)) {return old} ; return pens})
  }, [pens])
  
  // Current Pen
  const { index: currentPenIndex, name: currentPenName } = getPensFromSectionIdPenIndex(domain, systemId, activeSection?.section_id ?? 0, activePen?.location_id ?? 0)

  // Animals
  const animalsInSection = useAppSelector((state) => {if (currentSectionPens.length) {return selectByLocations(state.graphQlData.animals, currentSectionPens.map((pen) => pen.location_id))}}, isEqual) 
  // #endregion
  
  // #region | Reducer
  const [_state, dispatch] = useReducer<Reducer<ILfContext, LfDispatcherT>>(LfReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    systemId: systemId,
    sections: sections,
    selectedPens: [],
    penContent: 0,
    currentPenViewType: currentLfViewTypes.remainingToday,
    error: { Elementid: 0, errorText: "" }
  } as ILfContext);

  useEffect(() => {
    if (_state.sections?.length && sections.length && _state.sections.length !== sections.length) {
      dispatch({sections: sections});
    }
    if (_state.sections && _state.sections.length > 0) {
      return;
    }
    if (shouldDispatch) {
      setShouldDispatch(false);
      dispatch({ sections: sections });
    }
  }, [_state.sections, activeSection?.section_id, sections, shouldDispatch]);
  // #endregion
  
  // Handle Pen Click
  const handlePenClick = useCallback((location_id: Number) => {
    let pen = currentSectionPens.find((pen) => pen.location_id === location_id)
    if (!pen) return;
    return setActivePen(pen)
  }, [currentSectionPens])
  
  // #region | Section Click
  const handleSectionClick = (section_id: number) => {  
    const section = sections.find(s => s.section_id === section_id);
    if (!section) return;
  
    setActiveSection(section);
  };

  const section = useMemo(() => {
    if (!activeSection || !system) return null;
    return <SectionModal sectionId={activeSection.section_id} system={system} pens={currentSectionPens} onPenClick={(location_id: number) => handlePenClick(location_id)} animalsInSection={animalsInSection ?? []} connect={{ connection: connection, domain: domain, farm: farm }} />
  }, [activeSection, animalsInSection, connection, currentSectionPens, domain, farm, handlePenClick, system]);

  // #region | Toggles
  const toggleRouter = (viewType: "TODAY" | "YESTERDAY" | "ATTENTIONS") => {
    if (currentViewType === viewType) {
      setCurrentViewType("BASIC");
      dispatch({ currentPenViewType: currentLfViewTypes.basic });
      return;
    }
    setCurrentViewType(viewType);
  
    const dispatchMap = {
      TODAY: currentLfViewTypes.remainingToday,
      YESTERDAY: currentLfViewTypes.remainingYesterday,
      ATTENTIONS: currentLfViewTypes.attention,
    };
  
    dispatch({ currentPenViewType: dispatchMap[viewType] });
  };
  // #endregion

  // #region | Button List
  const ButtonRow = memo(({ editTable }: { editTable: boolean }) => {
    const [contentsRes, icons, onClicks] = contents(
      () => { setNewSectionModal(true) },
      () => { setEditSections(true); setCurrentViewType("BASIC"); dispatch({currentPenViewType: currentLfViewTypes.basic}) },
      () => { setEditSections(false); setCurrentViewType("TODAY"); dispatch({currentPenViewType: currentLfViewTypes.remainingToday}) },
      t("buttons.add", { defaultValue: "Add" }),
      t("buttons.edit", { defaultValue: "Edit" }),
      t("buttons.cancel", { defaultValue: "Cancel" }),
      editTable
    )
      
    return (
      <NewButtonList 
        contents={contentsRes} 
        onClick={(i) => { onClicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editTable ?[false, true, true] : [false, false] }
        overrides={(index) => {
          const { backgroundColor, opacity } = getButtonStyles(index, editTable);
          return {
            baseButton: { backgroundColor: backgroundColor },
            disabledStyles: { opacity: opacity, backgroundColor: backgroundColor }
          }
        }}
      />
    )
  })
  // #endregion
  
  if (!systemId) return null;
  return (
    <frames.Page_inner>
      <PageHeader topHeader={system?.name} subHeader={t(`menuHeaders.${Pages.freeda_lf_sections}`, {defaultValue: "sections"})}/>
      <frames._page_inner_content style={{zIndex: editSections ? "9999" : "0"}}>
        <AreaLayout
          zIndex={editSections ? 999 : 0}
          editableTable={editSections}
          headerArea={<p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary, marginBottom: "5px" }}>{sections.length} {t("locationFeederPages.sections.sections", { defaultValue: "sections" })}</p>}
          buttonArea={<ButtonRow editTable={editSections} />}
          topArea={<>
            <frames.ToggleBox style={{ columnGap: mobileXs ? "10px" : "30px" }}>
              <ToggleBox disabled={editSections ? true : false} text={t("feed.general.remainingToday", { defaultValue: "remaining today" })} icon={glyphs.TimeToday({ size: "17px" })} onChange={() => toggleRouter("TODAY")} isChecked={currentViewType === "TODAY" ? true : false} />
              <ToggleBox disabled={editSections ? true : false} text={t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" })} icon={glyphs.TimeYesterday({ size: "17px" })} onChange={() => toggleRouter("YESTERDAY")} isChecked={currentViewType === "YESTERDAY" ? true : false} />
              <ToggleBox disabled={editSections ? true : false} text={t("systemControls.general.attentions", { defaultValue: "attentions" })} icon={<div style={{ marginTop: "1px" }}><glyphs.Attention size="17px" /></div>} onChange={() => toggleRouter("ATTENTIONS")} isChecked={currentViewType === "ATTENTIONS" ? true : false} />
            </frames.ToggleBox>
            <NewSelect
              options={penContentOptions}
              fieldOverrides={{
                fieldStyle: NewFieldStyles.greySecondary,
                fieldWidth: "150px"
              }}
              valueOverrides={{
                defaultValue: penContentOptions[0]
              }}
              onChange={(value) => { dispatch({ penContent: Number(value?.id) }) }}
            />
          </>}
          contentArea={ 
            <LfContext.Provider value={{ state: _state, dispatch }}>
              {((sections.length === 0 || sections === undefined) && !loading) ? <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{t("locationFeederPages.sections.noSections", {defaultValue: "No sections found"})}</p> : <></>}
              {loading && (<div style={{width: "100%", height: "100%", minHeight: "250px", alignContent: "center", justifyContent: "center", gridColumn: "1/-1"}}><LoadingSpinner /></div> )}
              <frames.PenListsWrapper style={{ justifyContent: mobileXs ? "center" : "start" }}> 
                {(editSections && activeSection !== undefined) ? undefined : _state.sections?.map((section, index) => (
                  <div key={index} onClick={() => handleSectionClick(section.section_id)} style={{height: "fit-content", width: "fit-content"}}>
                    <LF_PENLIST
                      key={section.section_id}
                      section={section}
                    />
                  </div>
                ))}
              </frames.PenListsWrapper>
            </LfContext.Provider>
          }
          additionalArea={ <div style={{display: "flex", flexDirection: "column", gap: "5px"}}>
            <div style={{ display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px" }}>
              <p style={{ ...fonts.fontSize4, ...fonts.paragraph, ...fonts.fontWeight2, color: colors.whitePrimary, borderBottom: "2px solid "+colors.greySecondary+80 }}>{t("locationFeederPages.sections.section", { defaultValue: "Section" })}</p>
              <p style={{ ...fonts.fontSize4, ...fonts.paragraph, ...fonts.fontWeight2, color: colors.whitePrimary, borderBottom: "2px solid "+colors.greySecondary+80 }}>{t("locationFeederPages.sections.priority", { defaultValue: "Priority" })}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {sections.map((section) => (
                <div style={{display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px"}}>
                  <p style={{...fonts.fontSize4, ...fonts.paragraph, color: colors.greyLightTertiary, display: "flex", alignItems: "center"}}>{section.name}</p>
                  <NewInput
                    fieldOverrides={{
                      fieldType: NewFieldTypes.tertiaryField,
                      fieldStyle: NewFieldStyles.greySecondary,
                      fieldWidth: "60px"
                    }}
                    valueOverrides={{
                      valueType: "number",
                      defaultValue: section.panel_section_type // todo
                    }}
                  />
                </div>
              ))}
            </div>
          </div>}
          bluePrint={{
            disabled: editSections ? true : false,
            onClick: () => setBluePrintModal(true),
            startEnhancer: BluePrint({})
          }}
        />
      </frames._page_inner_content>

      {/* Section and pen Modals */}
      {!editSections && system && (activePen || activeSection)?
        <NewModal
          header={activePen !== undefined ? `${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })} ${currentPenName}` : currentSectionName}
          subHeader={activePen !== undefined ? `${system?.name} ${currentSectionName}` : `${system?.name}`}
          overrides={{
            backgroundColor: colors.greyLightTertiary,
            mainContentPadding: MainContentPaddingTypes.primary,
            mainContentScroll: true
          }}
          predefinedButtons={{
            onClose: () => {
              setActiveSection(undefined)
              if (activePen !== undefined) setActivePen(undefined)
            },
            onGoBack: activePen !== undefined ? () => setActivePen(undefined) : undefined,
            onPrev: () => activePen !== undefined
              ? setActivePen(currentSectionPens[(currentPenIndex - 1 + currentSectionPens?.length) % currentSectionPens.length])
              : setActiveSection(sections[(currentSectionIndex - 1 + sections.length) % sections.length]),
            onNext: () => activePen !== undefined
              ? setActivePen(currentSectionPens[(currentPenIndex + 1) % currentSectionPens.length])
              : setActiveSection(sections[(currentSectionIndex + 1 ) % sections.length]),
          }}
          mainContent={
            <div style={{ width: "735px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              {activePen === undefined && activeSection ?
                  <>{section}</>
                : activePen !== null ?
                  <PenModal activePen={activePen} system={system} /> // PEN POPUP
                : null
              }
            </div>
          }
        />
      : <></>}

      {/* Blue Print Modal */}
      {system && bluePrintModal && (
        <BluePrintModal system={system} onClose={() => setBluePrintModal(false)}/>
      )}
      
      {newSectionModal && (
        <NewSectionModal 
          closeModal={() => {setNewSectionModal(false);}}
          systemId={systemId}
          systemType={SystemTypes.freedaLf}
        />
      )}
      {editSections && (activeSection === undefined ? <Overlay /> :
        <LfContext.Provider value={{ state: _state, dispatch }}>
          <EditSectionModal
            closeModal={() => { setActiveSection(undefined); setEditSections(false); dispatch({ currentPenViewType: currentLfViewTypes.remainingToday, selectedPens: [] }); toggleRouter("TODAY") }}
            section={activeSection}
          />
        </LfContext.Provider>
      )}
    </frames.Page_inner>
  )
}